import { List } from 'immutable';
import { Job } from './job.model';
import { combineActions, handleActions } from 'redux-actions';
import {
  ActionsEnum,
  IAddAttachmentError,
  IAddAttachmentSuccess,
  IAddEstimateError,
  IAddEstimateSuccess,
  ISaveNoteSuccess,
  IApproveEstimateSuccess,
  ICreateJobError,
  ICreateJobSuccess,
  IDeleteAttachmentSuccess,
  IDeleteEstimateError,
  IDeleteEstimateSuccess,
  IFetchAttachmentError,
  IFetchAttachmentSuccess,
  IFetchJobPortalDataSuccess,
  IFetchJobsError,
  ISetAttachmentPreview,
  ISetEstimatePreview,
  IActionError,
} from '../job-data/job-data.actions';
import { IFetchJobsSuccess } from './job-data.actions';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { JobPortalData } from './models/job-portal-data.model';
import { JobEstimate } from './models/submodels/job-estimate.model';
import { Attachments } from './models/submodels/attachments.model';
import { JobNote } from './models/submodels/job-note.model';

export const FEATURE_NAME = 'job-data';

export interface IState {
  jobs: List<Job>;
  /**
   * contains response from create a new job response
   */
  job: Job;
  /**
   * contains response from job portal data including conversation messages
   */
  jobPortalData: JobPortalData;
  /**
   * response from adding estimate to job
   */
  estimate: JobEstimate;
  /**
   * selected file to be displayed in preview area
   */
  preview: JobEstimate | Attachments;
  /**
   * store estimate that just been approved
   */
  estimateNumberApproved: string;
  /**
   * response from fetch attachment
   */
  attachment: Attachments;
  /**
   * response from note backend
   */
  note: JobNote;
  /**
   * state where save note is in progress
   */
  noteLoading: boolean;
  /**
   * anything about error
   */
  error: Error;
}

// initial state
export const initialState: IState = {
  jobs: null,
  job: null,
  jobPortalData: null,
  estimate: null,
  estimateNumberApproved: null,
  attachment: null,
  preview: null,
  note: null,
  noteLoading: false,
  error: null,
};

function selectEstimateForPreview(portalData: JobPortalData) {
  const { estimates } = portalData;

  if (estimates.size > 0) {
    return estimates.get(0);
  }

  return null;
}

const reducerFn = handleActions(
  {
    [ActionsEnum.FETCH_JOBS]: function (state: IState): IState {
      return {
        ...state,
        jobs: null,
        error: null,
      };
    },
    [ActionsEnum.FETCH_JOBS_SUCCESS]: function (
      state: IState,
      { payload }: IFetchJobsSuccess
    ): IState {
      return {
        ...state,
        jobs: payload.jobs,
        error: null,
      };
    },
    [ActionsEnum.FETCH_JOB_PORTAL_DATA]: function (state: IState): IState {
      return {
        ...state,
        ...initialState,
      };
    },
    [ActionsEnum.FETCH_JOB_PORTAL_DATA_SUCCESS]: function (
      state: IState,
      { payload }: IFetchJobPortalDataSuccess
    ): IState {
      const { jobPortalData } = payload;
      const { estimates } = jobPortalData;

      return {
        ...state,
        jobPortalData: jobPortalData,
        preview: estimates.size > 0 ? estimates.get(0) : null,
        error: null,
      };
    },
    [ActionsEnum.CREATE_JOB_SUCCESS]: function (
      state: IState,
      { payload }: ICreateJobSuccess
    ): IState {
      return {
        ...state,
        job: payload.job,
        jobs: null,
        error: null,
      };
    },
    [ActionsEnum.ADD_ESTIMATE]: function (state: IState): IState {
      return {
        ...state,
        estimate: null,
        error: null,
      };
    },
    [ActionsEnum.ADD_ESTIMATE_SUCCESS]: function (
      state: IState,
      { payload }: IAddEstimateSuccess
    ): IState {
      const newEstimate = payload.estimate;
      const newEstimateState: Partial<IState> = {
        estimate: newEstimate,
        preview: newEstimate,
      };

      // Handle add job estimate from create estimate flow where portal data is still null
      if (state.jobPortalData) {
        const { estimates } = state.jobPortalData;

        const filteredEstimates = estimates
          .filter((estimate) => estimate.documentId !== newEstimate.documentId)
          .toList();

        const firstItemIndex = 0;
        const newEstimateList = filteredEstimates.insert(
          firstItemIndex,
          newEstimate
        );
        const newJobPortalData = state.jobPortalData.set(
          'estimates',
          newEstimateList
        ) as JobPortalData;

        return {
          ...state,
          ...newEstimateState,
          jobPortalData: newJobPortalData,
        };
      }

      return {
        ...state,
        ...newEstimateState,
      };
    },
    [ActionsEnum.SET_ESTIMATE_PREVIEW]: function (
      state: IState,
      { payload }: ISetEstimatePreview
    ): IState {
      const selectedJobEstimate = state.jobPortalData.estimates.find(
        (estimate) => estimate.documentId === payload.documentId
      );
      return {
        ...state,
        preview: selectedJobEstimate,
      };
    },
    [ActionsEnum.SET_ATTACHMENT_PREVIEW]: function (
      state: IState,
      { payload }: ISetAttachmentPreview
    ): IState {
      return {
        ...state,
        preview: payload.attachment,
      };
    },
    [ActionsEnum.APPROVE_ESTIMATE]: function (state: IState): IState {
      return {
        ...state,
        estimateNumberApproved: null,
      };
    },
    [ActionsEnum.APPROVE_ESTIMATE_SUCCESS]: function (
      state: IState,
      { payload }: IApproveEstimateSuccess
    ): IState {
      const { estimates: currentEstimates } = state.jobPortalData;

      const selectedEstimateIndex = currentEstimates.findIndex(
        (estimate) => estimate.documentId === payload.documentId
      );

      const updatedEstimate = currentEstimates
        .get(selectedEstimateIndex)
        .set('approval_date', payload.approvalDate)
        .set('approved', true) as JobEstimate;

      const newEstimates = currentEstimates
        .slice(0, selectedEstimateIndex)
        .concat([updatedEstimate])
        .concat(
          currentEstimates.slice(
            selectedEstimateIndex + 1,
            currentEstimates.size
          )
        );

      const newJobPortalData = state.jobPortalData.set(
        'estimates',
        newEstimates
      ) as JobPortalData;

      return {
        ...state,
        jobPortalData: newJobPortalData,
        preview: updatedEstimate,
        estimateNumberApproved: updatedEstimate.documentNumber,
      };
    },
    [ActionsEnum.DELETE_ESTIMATE_SUCCESS]: function (
      state: IState,
      { payload }: IDeleteEstimateSuccess
    ): IState {
      const deletedEstimate = payload.estimate;
      const updatedEstimates = state.jobPortalData.estimates.filter(
        (estimate) => estimate.documentId !== deletedEstimate.documentId
      );

      const newJobPortalData = state.jobPortalData.set(
        'estimates',
        updatedEstimates
      ) as JobPortalData;

      const { preview } = state;
      const isDeletedEstimateInPreview =
        preview && 'documentId' in preview
          ? deletedEstimate.documentId === preview.documentId
          : false;

      return {
        ...state,
        jobPortalData: newJobPortalData,
        preview: isDeletedEstimateInPreview
          ? selectEstimateForPreview(newJobPortalData)
          : preview,
      };
    },
    [ActionsEnum.DELETE_ATTACHMENT_SUCCESS]: function (
      state: IState,
      { payload }: IDeleteAttachmentSuccess
    ): IState {
      const deletedAttachment = payload.attachment;
      const updatedAttachments = state.jobPortalData.attachments.filter(
        (attachment) => attachment.id !== deletedAttachment.id
      );

      const newJobPortalData = state.jobPortalData.set(
        'attachments',
        updatedAttachments
      ) as JobPortalData;

      const { preview } = state;
      const isDeletedAttachmentInPreview =
        preview && 'id' in preview
          ? deletedAttachment.id === preview.id
          : false;

      // The current issue is attachment list doesn't have `url`
      // so we couldn't fallback to any attachment there for preview,
      // fallback to estimate as the interim solution
      return {
        ...state,
        jobPortalData: newJobPortalData,
        preview: isDeletedAttachmentInPreview
          ? selectEstimateForPreview(newJobPortalData)
          : preview,
      };
    },
    [ActionsEnum.FETCH_ATTACHMENT_SUCCESS]: function (
      state: IState,
      { payload }: IFetchAttachmentSuccess
    ): IState {
      return {
        ...state,
        attachment: payload.attachment,
        preview: payload.attachment,
      };
    },
    [ActionsEnum.ADD_ATTACHMENT_SUCCESS]: function (
      state: IState,
      { payload }: IAddAttachmentSuccess
    ): IState {
      const newAttachment = payload.attachment;
      const { attachments } = state.jobPortalData;

      const filteredAttachments = attachments
        .filter((attachment) => attachment.id !== newAttachment.id)
        .toList();

      const firstItemIndex = 0;
      const newAttachmentList = filteredAttachments.insert(
        firstItemIndex,
        newAttachment
      );
      const newJobPortalData = state.jobPortalData.set(
        'attachments',
        newAttachmentList
      ) as JobPortalData;

      return {
        ...state,
        jobPortalData: newJobPortalData,
        preview: newAttachment,
      };
    },
    [ActionsEnum.SAVE_NOTE]: function (state: IState): IState {
      return {
        ...state,
        noteLoading: true,
      };
    },
    [ActionsEnum.SAVE_NOTE_SUCCESS]: function (
      state: IState,
      { payload }: ISaveNoteSuccess
    ): IState {
      const newJobPortalData = state.jobPortalData.set(
        'note',
        payload.note
      ) as JobPortalData;

      return {
        ...state,
        jobPortalData: newJobPortalData,
        note: payload.note,
        noteLoading: false,
      };
    },
    [combineActions(
      ActionsEnum.FETCH_JOBS_ERROR,
      ActionsEnum.CREATE_JOB_ERROR,
      ActionsEnum.ADD_ESTIMATE_ERROR,
      ActionsEnum.DELETE_ESTIMATE_ERROR,
      ActionsEnum.FETCH_ATTACHMENT_ERROR,
      ActionsEnum.ADD_ATTACHMENT_ERROR,
      ActionsEnum.ACTION_ERROR
    )]: function (
      state: IState,
      {
        payload,
      }:
        | IFetchJobsError
        | ICreateJobError
        | IAddEstimateError
        | IDeleteEstimateError
        | IFetchAttachmentError
        | IAddAttachmentError
        | IActionError
    ): IState {
      const { error } = payload;

      return {
        ...state,
        error: error,
      };
    },
  } as any,
  initialState
);

export function reducer(state: IState = initialState, action: any) {
  return reducerFn(state, action);
}

const selectFeature = createFeatureSelector<IState>(FEATURE_NAME);

export const jobs = createSelector(
  selectFeature,
  (state: IState) => state.jobs
);
export const job = createSelector(selectFeature, (state: IState) => state.job);
export const jobPortalData = createSelector(
  selectFeature,
  (state: IState) => state.jobPortalData
);
export const estimate = createSelector(
  selectFeature,
  (state: IState) => state.estimate
);
export const preview = createSelector(
  selectFeature,
  (state: IState) => state.preview
);
export const estimateNumberApproved = createSelector(
  selectFeature,
  (state: IState) => state.estimateNumberApproved
);
export const note = createSelector(
  selectFeature,
  (state: IState) => state.note
);
export const noteLoading = createSelector(
  selectFeature,
  (state: IState) => state.noteLoading
);
